import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { AppConstants } from "src/app/constants/app.constants";
import * as moment from "moment";
import { RoutingConstants } from "src/app/constants/routingConstants";
import { FiltersTableStateService } from "src/app/core/services/filters-table-state.service";
import jwtDecode from "jwt-decode";
import { PayloadJwt } from "../models/jwt-dto";

const TOKEN_KEY = "AuthToken";

@Injectable({
  providedIn: "root",
})
export class TokenService {
  roles: Array<string> = [];

  constructor(
    private router: Router,
    private filtersTableStateService: FiltersTableStateService
  ) {}

  public setToken(token: string): void {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.setItem(TOKEN_KEY, token);
  }

  public getToken(): string {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  public isLogged(): boolean {
    if (this.getToken()) {
      return true;
    }
    return false;
  }

  public getUserName(): string {
    if (!this.isLogged()) {
      return null;
    }

    const values = this.decodeToken();
    const username = values.sub;

    return username;
  }

  public getFullName(): string {
    if (!this.isLogged()) {
      return null;
    }

    const values = this.decodeToken();
    const fullname = values.nombre + " " + values.apellido1;

    return fullname;
  }

  public getRegionDelConcesionario(): string {
    if (!this.isLogged()) {
      return null;
    }

    const values = this.decodeToken();

    return values.regionDelConcesionario;
  }

  getNavbarData(): any {
    if (!this.isLogged()) {
      return null;
    }

    const values = this.decodeToken();
    const data = {
      nombre: values.nombre,
      apellido1: values.apellido1,
      apellido2: values.apellido2,
      perfiles: values.perfiles,
      descripcionesPerfiles: values.descripcionesPerfiles,
      lastLoginDate: new Date(values.fechaUltimoAcceso * 1000),
    };

    return data;
  }

  public isAdmin(): boolean {
    if (!this.isLogged()) {
      return false;
    }

    const values = this.decodeToken();
    const perfiles = values.perfiles;

    if (perfiles.indexOf("ADMIN") < 0) {
      return false;
    }

    return true;
  }

  public hasRole(role: string): boolean {
    const values = this.decodeToken();
    const condition = (element) => element == role;

    return values.perfiles.some(condition);
  }

  public getRoles(): string[] {
    const values = this.decodeToken();

    return values.perfiles;
  }

  public logOut(): void {
    window.sessionStorage.removeItem(TOKEN_KEY);
    this.router.navigate(["/login"]);
  }

  public validateExpirationPassword() {
    const values = this.decodeToken();
    var date = moment.unix(values.caducidadPwd);

    if (moment().isAfter(date)) {
      return true;
    } else {
      return false;
    }
  }

  public validateExpirationNearlyCertificate() {
    const values = this.decodeToken();
    let date = moment.unix(values.caducidad);
    let today = moment().add(
      AppConstants.alertCertificateDaysExpiration,
      "days"
    );

    if (today.isBefore(date)) {
      return { expired: true, dateExpiration: date };
    } else {
      return { expired: false, dateExpiration: date };
    }
  }

  public validateExpiratedCertificate() {
    const values = this.decodeToken();
    let date = moment.unix(values.caducidad);
    let today = moment();

    if (today.isBefore(date)) {
      return { expired: false, dateExpiration: date };
    } else {
      return { expired: true, dateExpiration: date };
    }
  }

  public hasExpirationCertificate() {
    const values = this.decodeToken();

    if (values.caducidad) {
      return true;
    } else {
      return false;
    }
  }

  public redirectPredefinedMailBox() {
    const values = this.decodeToken();

    switch (values.buzon) {
      case "Certificados":
        this.router.navigate([RoutingConstants.certificatesMailBox.list]);
        break;
      case "COC":
        this.router.navigate([RoutingConstants.cocMailBox.list]);
        break;
      case "Placa":
        this.router.navigate([RoutingConstants.placaMailBox.list]);
        break;
      default:
        this.router.navigate(["/"]);
        break;
    }
  }

  public setPersonalNumPages(): void {
    let values = this.decodeToken();

    this.filtersTableStateService.setPersonalSize(
      values.numeroLineas ? values.numeroLineas : 10
    );
  }

  private decodeToken(): PayloadJwt {
    const token = this.getToken();
    let payloadDecoded: PayloadJwt;
    try {
      payloadDecoded = jwtDecode(token);
    } catch (Error) {
      return null;
    }
    return payloadDecoded;
  }

  public tokenIsExpired(): boolean {
    let values = this.decodeToken();

    let iat = moment
      .unix(values.iat)
      .add(AppConstants.validationTokenHours, "hours");
    let now = moment();

    if (iat.isBefore(now)) {
      return true;
    } else {
      return false;
    }
  }
}
